import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';

export interface BaseDialogProps {
  open: boolean;
  onClose?: () => void;
  /** Title text/node. When omitted the header (and its close button) is hidden. */
  title?: React.ReactNode;
  /** Secondary line under the title. */
  subtitle?: React.ReactNode;
  /** Leading title icon — pass a sized MUI icon, e.g. `<SendIcon sx={{ fontSize: 20 }} />`. */
  icon?: React.ReactNode;
  /** Colour applied to the icon; defaults to the accent. */
  iconColor?: string;
  /** Maps 1:1 to MUI's Dialog widths — xs/sm/md/lg = 444/600/900/1200. */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  /** Hairlines around the content — for longer config dialogs. */
  dividers?: boolean;
  /** Block backdrop + Esc close (forced acknowledgement, e.g. session expired). */
  disableBackdropClose?: boolean;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * The design system's base modal shell (`components/feedback/Dialog`): a thin
 * wrapper over MUI `Dialog` that standardises the header (optional icon + title
 * + close), an optional subtitle, the `dividers` variant, and the action row —
 * so every app dialog composes this instead of re-rolling DialogTitle/Actions.
 */
export const BaseDialog: React.FC<BaseDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  iconColor,
  maxWidth = 'sm',
  fullWidth = true,
  dividers = false,
  disableBackdropClose = false,
  actions,
  children,
}) => {
  const { tokens } = useTheme();

  const handleClose = (_event: object, reason: string) => {
    if (
      disableBackdropClose &&
      (reason === 'backdropClick' || reason === 'escapeKeyDown')
    ) {
      return;
    }
    onClose?.();
  };

  const showClose = Boolean(onClose) && !disableBackdropClose;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableBackdropClose}
    >
      {title != null && (
        <DialogTitle component="div" sx={{ p: '16px 24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon && (
              <Box sx={{ display: 'flex', color: iconColor ?? tokens.accent }}>
                {icon}
              </Box>
            )}
            <Typography variant="h6" sx={{ flex: 1, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {showClose && (
              <IconButton
                onClick={() => onClose?.()}
                size="small"
                aria-label="close"
                sx={{ mr: -0.5, color: tokens.textTertiary }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
          {subtitle != null && (
            <Typography
              sx={{
                fontSize: 12.5,
                color: tokens.textSecondary,
                mt: 0.5,
                lineHeight: 1.6,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </DialogTitle>
      )}

      <DialogContent dividers={dividers}>{children}</DialogContent>

      {actions && (
        <DialogActions sx={{ p: dividers ? '16px 24px' : '8px 12px 12px' }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};
